import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { money } from "@/lib/format";

// Narad chart palette: series order Kawaii -> Navy -> Floral, Other = control grey.
export const KAWAII = "#788CE3";
export const NAVY = "#17243F";
export const FLORAL = "#92BAD5";
export const OTHER = "#8A91A0";
export const POS = "#2E9E5B";
export const NEG = "#E5484D";
const GRID = "#E3DED0";
const INK2 = "#5B6577";
export const SERIES = [KAWAII, NAVY, FLORAL, OTHER];

function weekLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

const tooltipStyle = {
  background: "#fff",
  border: `1px solid ${GRID}`,
  borderRadius: 10,
  fontSize: 13,
  color: NAVY,
  boxShadow: "0 8px 32px rgba(23,36,63,0.16)",
};

export function LineTrend({ data, series = [{ key: "active_users", name: "Active users", color: KAWAII }], testid }) {
  const hasRight = series.some((s) => s.axis === "right");
  const tickInterval = data && data.length > 8 ? Math.ceil(data.length / 6) : 0;
  return (
    <div data-testid={testid}>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: hasRight ? 8 : 12, left: -4, bottom: 4 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="week" tickFormatter={weekLabel} tick={{ fontSize: 12, fill: INK2 }} tickLine={false} axisLine={{ stroke: GRID }} interval={tickInterval} minTickGap={16} tickMargin={8} />
          <YAxis yAxisId="left" tick={{ fontSize: 12, fill: INK2 }} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
          {hasRight && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: INK2 }} tickLine={false} axisLine={false} allowDecimals={false} width={44} />}
          <RTooltip contentStyle={tooltipStyle} labelFormatter={weekLabel} />
          {series.map((s) => (
            <Line
              key={s.key}
              yAxisId={s.axis === "right" ? "right" : "left"}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={2.25}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-4 mt-2 justify-center">
        {series.map((s) => (
          <span key={s.key} className="text-[12px] text-nb-ink2 inline-flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded" style={{ backgroundColor: s.color }} /> {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

export function BarRanking({ data, color = KAWAII, testid }) {
  const height = Math.max(120, data.length * 42 + 16);
  return (
    <div data-testid={testid}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, left: 8, bottom: 4 }} barCategoryGap={10}>
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={160}
            tick={{ fontSize: 13, fill: NAVY }}
            tickLine={false}
            axisLine={false}
          />
          <RTooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(120,140,227,0.10)" }} />
          <Bar dataKey="value" name="Value" radius={[0, 6, 6, 0]} fill={color} label={{ position: "right", fontSize: 12, fill: INK2 }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function HeatGrid({ rows, apps, testid }) {
  const cellColor = (v) => `rgba(120,140,227,${0.12 + Math.min(1, v) * 0.72})`;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="border-separate" style={{ borderSpacing: 4 }}>
        <thead>
          <tr>
            <th className="text-left text-[13px] text-nb-ink2 font-medium pr-3 align-bottom min-w-[140px]">Team</th>
            {apps.map((a) => (
              <th key={a.id} className="text-[12px] text-nb-ink2 font-medium px-1 pb-1 whitespace-nowrap">
                {a.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.team_id}>
              <td className="text-[13px] text-nb-ink pr-3 whitespace-nowrap">{r.team}</td>
              {r.cells.map((c) => (
                <td key={c.app_id} className="p-0">
                  <div
                    className="w-14 h-10 rounded-md flex items-center justify-center text-[12px] text-nb-ink tnum"
                    style={{ backgroundColor: cellColor(c.adoption) }}
                    title={`${r.team} · ${c.app}: ${Math.round(c.adoption * 100)}% adoption`}
                  >
                    {Math.round(c.adoption * 100)}%
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdoptionRanking({ rows, firm, testid }) {
  const big = rows.filter((r) => !r.group_too_small);
  const sorted = [...big].sort((a, b) => b.adoption - a.adoption);
  const firmPct = Math.round((firm || 0) * 100);
  if (sorted.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-4 text-[12px] text-nb-ink2 mb-4">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KAWAII }} /> At or above firm average</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: FLORAL }} /> Below firm average</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: NAVY }} /> Firm {firmPct}%</span>
      </div>
      <div className="relative space-y-2.5">
        {sorted.map((t) => {
          const p = Math.round(t.adoption * 100);
          const above = t.adoption >= (firm || 0);
          return (
            <div key={t.team_id} className="flex items-center gap-3">
              <span className="w-[150px] shrink-0 text-[13px] text-nb-ink truncate text-left">{t.team}</span>
              <span className="flex-1 relative h-6 rounded-md" style={{ background: "var(--bg-sunken)" }}>
                <span className="absolute inset-y-0 left-0 rounded-md transition-all" style={{ width: `${p}%`, background: above ? KAWAII : FLORAL }} />
                <span className="absolute inset-y-0 border-l border-dashed" style={{ left: `${firmPct}%`, borderColor: NAVY }} />
              </span>
              <span className="w-[72px] shrink-0 text-right text-[13px] text-nb-ink2 tnum">
                {p}% · {t.members_using}/{t.members}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PieDonut({ data, money: isMoney = true, testid }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div data-testid={testid}>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2} stroke="none">
            {data.map((_, i) => <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
          </Pie>
          <RTooltip contentStyle={tooltipStyle} formatter={(v) => (isMoney ? money(v) : Number(v).toLocaleString())} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center mt-1">
        {data.map((d, i) => (
          <span key={d.name} className="text-[12px] text-nb-ink2 inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SERIES[i % SERIES.length] }} />
            {d.name} · {Math.round((d.value / total) * 100)}%
          </span>
        ))}
      </div>
    </div>
  );
}

export function RadialRing({ value, size = 92, label, testid }) {
  const v = Math.max(0, Math.min(1, value || 0));
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const ringColor = v >= 0.7 ? KAWAII : v >= 0.4 ? FLORAL : OTHER;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-sunken)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v)}
          style={{ transition: "stroke-dashoffset 500ms ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-[18px] font-bold text-nb-ink tnum leading-none">{Math.round(v * 100)}%</span>
        {label && <span className="text-[10px] text-nb-ink2 mt-0.5">{label}</span>}
      </div>
    </div>
  );
}

export function MiniBars({ items, testid }) {
  const max = Math.max(1, ...items.map((i) => i.uses));
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((it) => (
        <div key={it.name} className="flex items-center gap-2">
          <span className="w-24 text-[12px] text-nb-ink2 truncate" title={it.name}>{it.name}</span>
          <span className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-sunken)" }}>
            <span className="block h-full rounded-full" style={{ width: `${(it.uses / max) * 100}%`, background: KAWAII }} />
          </span>
          <span className="text-[11px] text-nb-ink2 tnum w-8 text-right">{it.uses}</span>
        </div>
      ))}
    </div>
  );
}

export function WeeklyBars({ data, testid }) {
  const max = Math.max(1, ...data.map((d) => d.uses + d.active_minutes / 5));
  return (
    <div className="flex items-end gap-1.5 h-24" data-testid={testid}>
      {data.map((d) => {
        const val = d.uses + d.active_minutes / 5;
        return (
          <div key={d.week} className="flex-1 flex flex-col items-center justify-end" title={`${d.uses} uses · ${d.active_minutes} active minutes`}>
            <div
              className="w-full rounded-t-md"
              style={{ height: `${(val / max) * 100}%`, backgroundColor: val > 0 ? KAWAII : GRID, minHeight: 2 }}
            />
          </div>
        );
      })}
    </div>
  );
}
